import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { KeycloakRoleResponse } from '@app/api/user-service/model/keycloakRoleResponse';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { JobRoleMappingService } from '../../services/job-role-mapping.service';

export interface AddRolesDialogData {
  odooJobId: number;
  jobTitle?: string;
  /** Role names already mapped to the job; shown checked + disabled. */
  currentRoleNames?: string[];
}

export interface AddRolesDialogResult {
  /** Role names the admin chose to add. */
  added: string[];
}

@Component({
  selector: 'app-add-roles-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    AppButtonComponent,
    DataTableComponent,
  ],
  templateUrl: './add-roles-dialog.component.html',
  styleUrls: ['./add-roles-dialog.component.css'],
})
export class AddRolesDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly jobRoleMappingService = inject(JobRoleMappingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  loading = signal(false);

  filteredRoles = signal<KeycloakRoleResponse[]>([]);
  selectedRoles: KeycloakRoleResponse[] = [];
  searchTerm = '';

  /** Search is server-side so roles beyond the first page remain reachable. */
  private readonly search$ = new Subject<string>();

  /** Role names already mapped — rendered as checked and disabled. */
  private existingRoleNames = new Set<string>();

  columns: TableColumn[] = [
    {
      label: 'configuration.job_role_mapping.dialog.table.role_code',
      prop: 'name',
      type: 'text',
      align: 'left',
      sortable: false,
    },
    {
      label: 'configuration.job_role_mapping.dialog.table.description',
      prop: 'description',
      type: 'text',
      align: 'left',
      sortable: false,
    },
  ];

  isRoleDisabled = (row: KeycloakRoleResponse): boolean =>
    this.existingRoleNames.has(row?.name || '');

  isRoleSelected = (row: KeycloakRoleResponse): boolean => {
    if (this.isRoleDisabled(row)) {
      return true;
    }
    return this.selectedRoles.some((r) => r.name === row.name);
  };

  ngOnInit(): void {
    const data = (this.config?.data || {}) as AddRolesDialogData;
    if (data.odooJobId == null) {
      this.dialogRef.close();
      return;
    }
    this.existingRoleNames = new Set(
      (data.currentRoleNames || []).filter(Boolean),
    );

    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.loading.set(true);
          return this.jobRoleMappingService.getKeycloakRoles(0, 1000, term);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ data: roles }) => {
          this.filteredRoles.set(roles);
          this.loading.set(false);
        },
        error: () => {
          this.filteredRoles.set([]);
          this.loading.set(false);
        },
      });

    // Initial (unfiltered) load.
    this.search$.next('');
  }

  onSearch(): void {
    this.search$.next(this.searchTerm.trim());
  }

  onSelectionChange(selection: KeycloakRoleResponse[]): void {
    // Exclude already-mapped (disabled) roles from the new selection.
    this.selectedRoles = (selection || []).filter(
      (role) => !!role?.name && !this.existingRoleNames.has(role.name),
    );
  }

  canConfirm(): boolean {
    return this.selectedRoles.length > 0;
  }

  confirmLabel(): string {
    const base = this.translate.instant(
      'configuration.job_role_mapping.actions.add_roles',
    );
    const count = this.selectedRoles.length;
    return count > 0 ? `${base} (${count})` : base;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const added = this.selectedRoles
      .map((role) => role.name)
      .filter((name): name is string => !!name);
    if (added.length === 0) {
      this.dialogRef.close();
      return;
    }
    const result: AddRolesDialogResult = { added };
    this.dialogRef.close(result);
  }
}
